import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TopAlgorithmsChart from '../Chart/TopAlgorithmsChart/TopAlgorithmsChart';
import './Status.css';
import { FaTelegramPlane, FaDiscord, FaCoins, FaTrophy, FaServer, FaCalendarAlt, FaStar, FaLayerGroup, FaUsers, FaBitcoin, FaClock, FaChartLine, FaFire, FaDollarSign } from 'react-icons/fa';
import { Card, CardContent, Typography, Link, Button } from '@mui/material';

const getPoolImage = (poolName) => {
  const imageUrl = `/img/pools/${poolName.toLowerCase()}.webp`;
  return imageUrl;
};

const getCoinImage = (symbol) => {
  const sanitizedSymbol = symbol.split(/[-_]/)[0].toUpperCase();
  return `/img/coins/${sanitizedSymbol}.webp`;
};

const Status = () => {
  const [data, setData] = useState({
    recentCoins: [],
    newCoins: [],
    topGainers: [],
    topLosers: [],
    topPools: [],
    upcomingEvents: [],
    topRatedCoins: [],
    topCoinsWithMostPools: [],
    topAlgorithmsWithMostMiners: [],
    topCoinsWithMostMiners: [],
    topCoinsByBTC24h: [],
    totalBTCByAlgorithm: [],
    avgTimeBetweenBlocks: [],
    blockTimeVariability: [],
    topCoinsByBlockGrowth: [],
    avgRewardAndDifficultyByPool: []
  });

  useEffect(() => {
    axios.get('/api/status')
      .then(response => {
        setData(response.data || {});
      })
      .catch(error => {
        console.error('Error fetching status from API:', error);
      });
  }, []);

  return (
    <div className="status-container">
      <div className="row">

      <div className="col-md-12 d-flex flex-column flex-md-row align-items-start gap-4">
          {/* Welcome Card */}
          <Card className="welcome-card" sx={{ flex: 2, boxShadow: 3, borderRadius: 4, backgroundColor: '#f9f9fb' }}>
            <CardContent>
              <Typography variant="h4" component="div" gutterBottom style={{ fontWeight: 'bold', color: '#1d2936', textAlign: 'center' }}>
                Welcome to CoinStatsHub.tech!
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph style={{ textAlign: 'justify' }}>
                Thank you for joining <strong>CoinStatsHub.tech</strong>, a platform designed to provide you with essential, centralized cryptocurrency data.
                Our mission is to offer development teams, miners, investors, and users an efficient and reliable tool to make informed decisions with real-time data.
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph style={{ textAlign: 'justify' }}>
                Here, you’ll find valuable data on crypto projects, performance metrics, and promotional tools to increase your project’s visibility.
                Learn more <Link href="/about" color="primary">About us</Link>.
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph style={{ textAlign: 'justify' }}>
                We respect your privacy. The only information we collect and store is your email address, used solely for registration purposes. 
                Read our <Link href="/privacy" color="primary">Privacy Policy</Link>.
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph style={{ textAlign: 'justify' }}>
                By using our service, you accept the terms outlined in our <Link href="/terms" color="primary">Terms of Service</Link>. 
                Additionally, all payments made in FUEC for promotions are directly burned at our official burn address, benefiting the entire FuerteCoin community. 
                Service provided by <Link href="https://fuertecoin.network" target="_blank" rel="noopener noreferrer" color="primary">FuerteCoin</Link>.
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph style={{ textAlign: 'justify' }}>
                Thank you for trusting <strong>CoinStatsHub.tech</strong>. We are excited to support you on your journey!
              </Typography>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <img
                  src="/img/logo.webp"
                  alt="CoinStatsHub Logo"
                  style={{ maxWidth: '250px', height: 'auto', borderRadius: '10px', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>

              <Typography variant="h6" gutterBottom style={{ textAlign: 'center', marginTop: '20px', fontWeight: 'bold' }}>
                Connect with our community:
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '10px' }}>
                <Link href="https://discord.gg/jvjCjRCEX2" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#5865F2' }}>
                  <FaDiscord size={30} />
                </Link>
                <Link href="https://t.me/Fuertecoin" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#0088CC' }}>
                  <FaTelegramPlane size={30} />
                </Link>
              </div>
            </CardContent>
          </Card>
          {/* Promotion Card */}
          <Card className="promotion-card"
            sx={{
              flex: 1,
              boxShadow: 6,
              borderRadius: 6,
              overflow: 'hidden',
              position: 'relative',
              backgroundImage: "url('/img/logo.webp')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundBlendMode: 'multiply',
              backgroundColor: '#ff9800',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CardContent
              sx={{
                position: 'relative',
                width: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                padding: '20px',
              }}
            >
              <Typography
                variant="h4"
                component="div"
                gutterBottom
                sx={{
                  fontWeight: 'bold',
                  color: '#ffd700',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.6)',
                  fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
                }}
              >
                🚀 Promote Your Project Today!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  marginBottom: '15px',
                  color: '#f5f5f5',
                }}
              >
                Registered users can showcase their projects, websites or advertisements with our exclusive promotional packages, designed to provide maximum visibility and reach a target audience interested in cryptocurrencies and blockchain.
                Payments are made in FUEC and burned at the official address{' '}
                <a 
                  href="https://burning.fuertecoin.network/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ color: '#00bcd4', textDecoration: 'underline' }}
                >
                  https://burning.fuertecoin.network/
                </a>, strengthening the Fuertecoin ecosystem while promoting your initiatives and avoiding scams in promotions. Boost your project and achieve greater success with us!
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.5,
                  marginBottom: '25px',
                  color: '#ffcccb',
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                }}
              >
                <strong>Basic Package:</strong> 250x50 ad for 7 days.<br />
                <strong>Intermediate Package:</strong> Leaderboard ad (728x90) for 14 days.<br />
                <strong>Premium Package:</strong> Skyscraper ad (160x600) for 30 days.<br />
                <strong>Gold Package:</strong> Large top-of-page ad for 60 days.<br />
                <strong>Silver Package:</strong> Medium sidebar ad (300x250) for 15 days.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  fontSize: { xs: '0.8rem', sm: '1rem', md: '1.2rem' },
                  padding: { xs: '8px 20px', sm: '10px 25px', md: '12px 30px' },
                  color: '#fff',
                  backgroundColor: '#29b6f6',
                  '&:hover': {
                    backgroundColor: '#0288d1',
                  },
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
                  width: { xs: '90%', sm: 'auto' },
                }}
                href="/dashboard"
              >
                Select Your Package Now!
              </Button>
            </CardContent>
          </Card>
          {/* Upcoming Events */}
          <div className="status-card" style={{ flex: 1 }}>
            <div className="status-card-header">
              <FaCalendarAlt className="status-icon" /> Upcoming Events
            </div>
            <div className="status-card-body">
              {data.upcomingEvents.length === 0 ? (
                <p className="status-empty-message">No upcoming events</p>
              ) : (
                data.upcomingEvents.map((event, index) => (
                  <div key={index} className="status-event-item">
                    <a href={`/coin/${event.symbol}`}>
                      <img
                        src={getCoinImage(event.symbol)}
                        alt={event.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>
                      <a href={`/coin/${event.symbol}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                        {event.symbol}
                      </a> - {event.event_type.replace('_', ' ')}
                    </strong>
                    <p>
                      Blocks: {event.block_range_start} - {event.block_range_end}<br />
                      Value: {parseFloat(event.value_start)} {event.event_type === 'block_reward' ? 'Reward' : 'Collateral'}
                    </p>
                    <p>
                      Current height: {event.height}<br />
                      Source: {event.event_source === 'specific' ? 'Specific' : 'Default'}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
{/* Top Algorithms Chart */}
<div style={{ marginTop: '20px' }}>
  <TopAlgorithmsChart />
</div>
        {/* Top Rated Coins */}
        <div className="col-md-3">
          <div className="status-card status-highlighted-card">
            <div className="status-card-header">
              <FaStar className="status-icon" /> Top Rated Coins
            </div>
            <div className="status-card-body">
              {data.topRatedCoins.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.topRatedCoins.map((coin, index) => (
                  <div
                    key={index}
                    className={`status-coin-item ${index === 0 ? 'status-first-coin' : ''}`}
                  >
                    <div className="status-coin-rank">
                      <span className={`status-rank-number ${index === 0 ? 'status-first-rank' : ''}`}>{index + 1}</span>
                      <FaStar className={`status-star-icon ${index === 0 ? 'status-large-star' : 'gold'}`} /> {parseFloat(coin.average_rating).toFixed(2)}
                    </div>
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className={`status-coin-image ${index === 0 ? 'status-large-coin-image' : ''}`}
                      />
                    </a>
                    <strong className={`status-coin-symbol ${index === 0 ? 'status-highlighted-symbol' : ''}`}>
                      <a href={`/coin/${coin.symbol}`}>
                        {coin.symbol}
                      </a>
                    </strong>
                    <span className={`status-rating ${index === 0 ? 'status-highlighted-rating' : ''}`}>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* New Coins */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaCoins className="status-icon" /> New Coins
            </div>
            <div className="status-card-body">
              {data.newCoins?.length === 0 ? (
                <p className="status-empty-message">No data available</p>
              ) : (
                data.newCoins?.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    {/* Imagen de la moneda */}
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>

                    {/* Nombre y símbolo de la moneda */}
                    <strong className="coin-name">
                      <a href={`/coin/${coin.symbol}`}>
                        {coin.name} ({coin.symbol})
                      </a>
                    </strong>

                    {/* Algoritmo */}
                    <p className="coin-algo">
                      <FaLayerGroup className="algo-icon" />
                      <a href={`/algorithmhardware/${coin.algo.toLowerCase()}`}>
                        {coin.algo}
                      </a>
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Recent Coins */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaCoins className="status-icon" /> Recent Coins
            </div>
            <div className="status-card-body">
              {data.recentCoins.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.recentCoins.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.name}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>
                      <a href={`/coin/${coin.symbol}`}>
                        {coin.name} ({coin.symbol})
                      </a>
                    </strong>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Top Gainers */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaTrophy className="status-icon" /> Top Gainers (24h)
            </div>
            <div className="status-card-body">
              {data.topGainers.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.topGainers.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.primaryTicker}`}>
                      <img
                        src={getCoinImage(coin.primaryTicker)}
                        alt={coin.primaryName}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>
                      <a href={`/coin/${coin.primaryTicker}`}>
                        {coin.primaryName}
                      </a>
                    </strong>
                    <span className={`status-percent ${coin.changePercent >= 0 ? 'status-positive' : 'status-negative'}`}>
                      {parseFloat(coin.changePercent).toFixed(2)}%
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Top Losers */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaTrophy className="status-icon status-icon-loser" /> Top Losers (24h)
            </div>
            <div className="status-card-body">
              {data.topLosers.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.topLosers.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.primaryTicker}`}>
                      <img
                        src={getCoinImage(coin.primaryTicker)}
                        alt={coin.primaryName}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>
                      <a href={`/coin/${coin.primaryTicker}`}>
                        {coin.primaryName}
                      </a>
                    </strong>
                    <span className={`status-percent status-negative`}>
                      {parseFloat(coin.changePercent).toFixed(2)}%
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Top Pools */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaServer className="status-icon" /> Top Pools
            </div>
            <div className="status-card-body">
              {data.topPools.length === 0 ? (
                <p>No data available</p>
              ) : (
                <ul className="status-pool-list">
                  {data.topPools.map((pool, index) => (
                    <li key={index} className="status-pool-item">
                      <a href={`/pool/${pool.pool_name}`}>
                        <img
                          src={getPoolImage(pool.pool_name)}
                          alt={pool.pool_name}
                          onError={(e) => { e.target.src = '/img/pools/default.webp'; }}
                          className="status-pool-image"
                        />
                      </a>
                      <strong>
                        <a href={`/pool/${pool.pool_name}`}>
                          {pool.pool_name}
                        </a>
                      </strong>
                      <span>Coins: {pool.total_monedas}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Algorithms Overview */}
        <div className="col-md-3">
          <div className="status-card status-highlighted-card">
            <div className="status-card-header">
              <FaUsers className="status-icon" /> Algorithms Overview
            </div>
            <div className="status-card-body">

              {/* Algorithms with Most Miners */}
              <div className="status-subsection">
                <h5 className="status-subsection-header">
                  <FaLayerGroup className="subsection-icon" /> Algorithms with Most Miners
                </h5>
                {data.topAlgorithmsWithMostMiners?.length === 0 ? (
                  <p className="status-empty-message">No data available</p>
                ) : (
                  data.topAlgorithmsWithMostMiners?.map((algo, index) => (
                    <div key={index} className="status-algo-item">
                      <FaFire className="algo-icon fire-icon" />
                      <strong className="algo-name">
                        <a href="/algorithmList">{algo.algo}</a>
                      </strong>
                      <span className="algo-miners">
                        Miners: <span className="highlight">{algo.total_miners}</span>
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* BTC by Algorithm */}
              <div className="status-subsection">
                <h5 className="status-subsection-header">
                  <FaBitcoin className="subsection-icon" /> BTC by Algorithm
                </h5>
                {data.totalBTCByAlgorithm?.length === 0 ? (
                  <p className="status-empty-message">No data available</p>
                ) : (
                  data.totalBTCByAlgorithm?.map((algo, index) => (
                    <div key={index} className="status-algo-item">
                      <FaDollarSign className="algo-icon dollar-icon" />
                      <strong className="algo-name">
                        <a href="/algorithmList">{algo.algo}</a>
                      </strong>
                      <span className="algo-btc">
                        BTC: <span className="highlight">{algo.total_btc_generated ? parseFloat(algo.total_btc_generated).toFixed(8) : '0.00000000'}</span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Monedas con más pools */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaLayerGroup className="status-icon" /> Coins with Most Pools
            </div>
            <div className="status-card-body">
              {data.topCoinsWithMostPools.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.topCoinsWithMostPools.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>
                      <a href={`/coin/${coin.symbol}`}>
                        {coin.name} ({coin.symbol})
                      </a>
                    </strong>
                    <span>Pools: {coin.pool_count}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Top Coins by BTC Generated in 24h */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaBitcoin className="status-icon" /> Top Coins by BTC (24h)
            </div>
            <div className="status-card-body">
              {data.topCoinsByBTC24h?.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.topCoinsByBTC24h?.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>{coin.name} ({coin.symbol})</strong>
                    <span>
                      BTC: {parseFloat(coin.btc_last_24h).toFixed(8)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Avg Time Between Blocks for Coins */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaClock className="status-icon" /> Avg Time Between Blocks (Last Week)
            </div>
            <div className="status-card-body">
              {data.avgTimeBetweenBlocks?.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.avgTimeBetweenBlocks?.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>{coin.name} ({coin.symbol})</strong>
                    <span>
                      Avg Time: {coin.avg_time_between_blocks ? parseFloat(coin.avg_time_between_blocks).toFixed(2) : '0.00'} sec
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {/* Block Time Variability */}
        <div className="col-md-3">
          <div className="status-card">
            <div className="status-card-header">
              <FaChartLine className="status-icon" /> Block Time Variability
            </div>
            <div className="status-card-body">
              {data.blockTimeVariability?.length === 0 ? (
                <p>No data available</p>
              ) : (
                data.blockTimeVariability?.map((coin, index) => (
                  <div key={index} className="status-coin-item">
                    <a href={`/coin/${coin.symbol}`}>
                      <img
                        src={getCoinImage(coin.symbol)}
                        alt={coin.symbol}
                        onError={(e) => { e.target.src = '/img/coins/DEFAULT.webp'; }}
                        className="status-coin-image"
                      />
                    </a>
                    <strong>{coin.name} ({coin.symbol})</strong>
                    <span>
                      Variability: {coin.block_time_variability ? parseFloat(coin.block_time_variability).toFixed(2) : '0.00'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default Status;
